'use client';

import { AuthForm } from '@/components/blocks/auth/auth-form';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { XIcon } from 'lucide-react';
import { usePathname, useSearchParams } from 'next/navigation';

interface SignupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  forceOpen?: boolean;
}

export default function SignupModal({ open, onOpenChange, forceOpen = false }: SignupModalProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const callbackUrl = searchParams.get('callbackUrl');

  return (
    <Dialog open={open} onOpenChange={forceOpen ? () => {} : onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="!z-[2147483640] mx-auto w-[calc(100%-2rem)] max-w-[400px] rounded-2xl border-0 bg-[#212120] p-0 text-white shadow-2xl sm:max-w-[400px]"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          document.getElementById('google-auth-button')?.focus();
        }}
        onPointerDownOutside={(e) => {
          if (forceOpen) {
            e.preventDefault();
            return;
          }
          const target = e.target as HTMLElement;
          if (target.closest('#cookie-consent-modal')) {
            e.preventDefault();
          }
        }}
        onInteractOutside={(e) => {
          if (forceOpen) {
            e.preventDefault();
            return;
          }
          const target = e.target as HTMLElement;
          if (target.closest('#cookie-consent-modal')) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          if (forceOpen) e.preventDefault();
        }}
      >
        <VisuallyHidden>
          <DialogTitle>Sign Up</DialogTitle>
          <DialogDescription>
            Join ZappingAI to start creating amazing AI videos and images.
          </DialogDescription>
        </VisuallyHidden>
        <div className="!p-8 relative flex w-full flex-col items-center">
          {!forceOpen && (
            <DialogClose className="absolute top-5 right-5 z-50 cursor-pointer rounded-full bg-white/10 p-2 text-white/70 transition-colors hover:bg-white/20 hover:text-white focus:outline-none">
              <XIcon className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </DialogClose>
          )}
          <div className="flex max-h-[85vh] w-full justify-center overflow-y-auto">
            <div className="w-full">
              <AuthForm
                variant="modal"
                callbackURL={callbackUrl || pathname}
                onSuccess={() => onOpenChange(false)}
              />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
